import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, InputGroup, Dropdown, Modal, Spinner } from 'react-bootstrap';
import { FaFileDownload, FaSearch, FaCalendarAlt, FaChartBar, FaMapMarkerAlt, FaClock, FaTachometerAlt, FaExclamationTriangle, FaSync, FaRoute } from 'react-icons/fa';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GetFromApi } from '../utils/api';
import { getRouteFromOSRM } from '../utils/routing';

// Fix for marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const Reports = () => {
  const [reportType, setReportType] = useState('checkSpeed');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    startTime: '12:00',
    endTime: '11:55',
    startAmPm: 'AM',
    endAmPm: 'PM'
  });
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [generatedReport, setGeneratedReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [routeData, setRouteData] = useState([]);
  const [snappedRoute, setSnappedRoute] = useState([]);

  const reportTypes = [
    { id: 'checkSpeed', name: 'Check Speed', icon: <FaTachometerAlt /> },
    { id: 'fullReport', name: 'Full Report', icon: <FaChartBar /> },
    { id: 'geofence', name: 'Geofence Report', icon: <FaMapMarkerAlt /> },
    { id: 'ignition', name: 'Ignition Report', icon: <FaClock /> },
    { id: 'travel', name: 'Travel Report', icon: <FaRoute /> },
    { id: 'speedViolation', name: 'Speed Violation Report', icon: <FaExclamationTriangle /> },
  ];

  // Fetch vehicles on mount
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await GetFromApi('/vehicles');
        if (response.success) {
          setVehicles(response.data);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    };
    fetchVehicles();

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setDateRange(prev => ({
      ...prev,
      startDate: formattedDate,
      endDate: formattedDate
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (['startDate', 'endDate', 'startTime', 'endTime', 'startAmPm', 'endAmPm'].includes(name)) {
      setDateRange(prev => ({ ...prev, [name]: value }));
    } else if (name === 'vehicle') {
      setSelectedVehicle(value);
    }
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setGeneratedReport(false);

    try {
      const endpoint = `/reports/${reportType}?vehicleId=${selectedVehicle}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      const response = await GetFromApi(endpoint);
      
      if (response.success) {
        setReportData(response.data || []);
        // If it's a travel report, also get the route data
        if (reportType === 'travel' || reportType === 'fullReport') {
          const historyResponse = await GetFromApi(`/vehicles/${selectedVehicle}/location-history?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
          if (historyResponse.success) {
            const rawLocs = historyResponse.data || [];
            setRouteData(rawLocs);

            if (rawLocs.length > 1) {
              // Raw data is sorted DESC (newest first). Reverse it to make it chronological.
              const coords = rawLocs.map(p => [parseFloat(p.lat), parseFloat(p.lng)]).reverse();
              
              // Sample points to fit OSRM URL limits (approx. 80 points is safe)
              const maxPoints = 80;
              const step = Math.max(1, Math.floor(coords.length / maxPoints));
              const sampled = [];
              for (let i = 0; i < coords.length; i += step) {
                sampled.push(coords[i]);
              }
              // Ensure the last element is included
              if (sampled.length > 0 && sampled[sampled.length - 1] !== coords[coords.length - 1]) {
                sampled.push(coords[coords.length - 1]);
              }

              try {
                const roadRoute = await getRouteFromOSRM(sampled);
                setSnappedRoute(roadRoute);
              } catch (err) {
                console.warn('Could not fetch OSRM snapped route:', err);
                setSnappedRoute(coords); // Fallback to raw coords
              }
            } else {
              setSnappedRoute([]);
            }
          }
        }
        setGeneratedReport(true);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      // Still show the report section with sample data for demo
      setReportData(getSampleData());
      setGeneratedReport(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getSampleData = () => {
    switch (reportType) {
      case 'checkSpeed':
        return [
          { srNo: 1, location: 'Auto Market Rd, Hisar, Haryana', dateTime: new Date().toLocaleString(), speed: 45, odometer: '125.5 Km', lat: 29.1566, lng: 75.7226 },
          { srNo: 2, location: 'NH 65, Hisar, Haryana', dateTime: new Date().toLocaleString(), speed: 62, odometer: '138.2 Km', lat: 29.1612, lng: 75.7318 },
          { srNo: 3, location: 'Balsamand Rd, Hisar', dateTime: new Date().toLocaleString(), speed: 38, odometer: '142.8 Km', lat: 29.1489, lng: 75.7125 },
        ];
      case 'fullReport':
        return [
          { date: dateRange.startDate, vehicle: selectedVehicle ? vehicles.find(v => v.id.toString() === selectedVehicle)?.number : 'N/A', distance: '45.2 km', avgSpeed: '32 km/h', maxSpeed: '78 km/h', idleTime: '00:45:22', movingTime: '02:15:33', stoppedTime: '21:00:05' },
        ];
      default:
        return [];
    }
  };

  const handleViewMap = (item) => {
    setSelectedLocation(item);
    setShowMapModal(true);
  };

  const getCurrentReportTypeName = () => {
    return reportTypes.find(type => type.id === reportType)?.name || 'Select Report Type';
  };

  const renderReportContent = () => {
    if (reportData.length === 0) {
      return (
        <div className="text-center p-5">
          <FaChartBar size={50} className="mb-3 text-primary opacity-50" />
          <h5>No data found for the selected criteria</h5>
        </div>
      );
    }

    switch (reportType) {
      case 'checkSpeed':
        return (
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr className="bg-light">
                  <th>Sr. No.</th>
                  <th>Location</th>
                  <th>Date & Time</th>
                  <th>Speed</th>
                  <th>Odometer</th>
                  <th>Map View</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.srNo || index + 1}</td>
                    <td>{row.location || row.address || 'Unknown'}</td>
                    <td>{row.dateTime || new Date(row.timestamp).toLocaleString()}</td>
                    <td>{row.speed} km/h</td>
                    <td>{row.odometer}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" onClick={() => handleViewMap(row)}>
                        <FaMapMarkerAlt className="me-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      case 'fullReport':
      case 'travel':
        return (
          <>
            <div className="table-responsive mb-4">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vehicle</th>
                    <th>Distance</th>
                    <th>Avg Speed</th>
                    <th>Max Speed</th>
                    <th>Idle Time</th>
                    <th>Moving Time</th>
                    <th>Stopped Time</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.date}</td>
                      <td>{row.vehicle}</td>
                      <td>{row.distance}</td>
                      <td>{row.avgSpeed}</td>
                      <td>{row.maxSpeed}</td>
                      <td>{row.idleTime}</td>
                      <td>{row.movingTime}</td>
                      <td>{row.stoppedTime}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {/* Route Map */}
            <Card className="border">
              <Card.Header className="bg-light">
                <h6 className="mb-0"><FaRoute className="me-2" /> Route History</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <div style={{ height: '400px' }}>
                  <MapContainer 
                    center={routeData.length > 0 ? [parseFloat(routeData[routeData.length - 1].lat), parseFloat(routeData[routeData.length - 1].lng)] : [23.0225, 72.5714]} 
                    zoom={12} 
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {(snappedRoute.length > 0 || routeData.length > 1) && (
                      <Polyline
                        positions={snappedRoute.length > 0 ? snappedRoute : routeData.map(p => [parseFloat(p.lat), parseFloat(p.lng)]).reverse()}
                        pathOptions={{ color: '#3B82F6', weight: 4, opacity: 0.8 }}
                      />
                    )}
                    {routeData.length > 0 && (
                      <>
                        <Marker position={[parseFloat(routeData[routeData.length - 1].lat), parseFloat(routeData[routeData.length - 1].lng)]}>
                          <Popup>Start Point<br />{new Date(routeData[routeData.length - 1].timestamp).toLocaleString()}</Popup>
                        </Marker>
                        <Marker position={[parseFloat(routeData[0].lat), parseFloat(routeData[0].lng)]}>
                          <Popup>End Point<br />{new Date(routeData[0].timestamp).toLocaleString()}</Popup>
                        </Marker>
                      </>
                    )}
                  </MapContainer>
                </div>
              </Card.Body>
            </Card>
          </>
        );
      default:
        return (
          <div className="text-center p-5">
            <FaChartBar size={50} className="mb-3 text-primary opacity-50" />
            <h5>Select a report type and generate a report</h5>
          </div>
        );
    }
  };

  return (
    <Container fluid className="p-3">
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Vehicle Reports</h4>
          {isLoading && <Spinner size="sm" animation="border" variant="light" />}
        </Card.Header>
        <Card.Body className="p-4">
          <Form onSubmit={handleGenerateReport}>
            <Row className="align-items-end">
              <Col md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Select Vehicle</Form.Label>
                  <Form.Select name="vehicle" value={selectedVehicle} onChange={handleInputChange} required>
                    <option value="">Select a vehicle</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>{vehicle.number}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Report Type</Form.Label>
                  <Dropdown className="w-100">
                    <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start d-flex justify-content-between align-items-center">
                      <span>{getCurrentReportTypeName()}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {reportTypes.map(type => (
                        <Dropdown.Item key={type.id} onClick={() => setReportType(type.id)} active={reportType === type.id} className="d-flex align-items-center">
                          <span className="me-2">{type.icon}</span> {type.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>
              
              <Col md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Start Date</Form.Label>
                  <Form.Control type="date" name="startDate" value={dateRange.startDate} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
              
              <Col md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">End Date</Form.Label>
                  <Form.Control type="date" name="endDate" value={dateRange.endDate} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mt-2">
              <Col className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" className="me-2" /> : <FaSearch className="me-2" />}
                  View Report
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      
      {generatedReport && (
        <Card className="shadow-sm">
          <Card.Header className="bg-light d-flex justify-content-between align-items-center p-3">
            <h5 className="mb-0">
              <span className="me-2">{reportTypes.find(type => type.id === reportType)?.icon}</span>
              {getCurrentReportTypeName()}
            </h5>
            <div>
              <span className="me-3 fw-bold">
                Vehicle: {selectedVehicle ? vehicles.find(v => v.id.toString() === selectedVehicle)?.number : 'N/A'}
              </span>
              <Button variant="outline-primary" size="sm">
                <FaFileDownload className="me-1" /> Export
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {renderReportContent()}
          </Card.Body>
        </Card>
      )}

      {/* Location Map Modal */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Location View</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedLocation && (
            <div style={{ height: '400px' }}>
              <MapContainer 
                center={[selectedLocation.lat || 23.0225, selectedLocation.lng || 72.5714]} 
                zoom={15} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[selectedLocation.lat || 23.0225, selectedLocation.lng || 72.5714]}>
                  <Popup>
                    <strong>Speed:</strong> {selectedLocation.speed} km/h<br />
                    <strong>Time:</strong> {selectedLocation.dateTime}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Reports;